import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Controller, FormProvider } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DocumentAttribute,
  MultiSelectField,
  ProductReferenceField,
  UserReferenceField,
} from "../../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { ObjectReferenceField } from "../../../../../components/ObjectReference/ObjectReferenceField";
import { LinkAttributeField } from "../../../../../components/LinkAttributeField/LinkAttributeField";
import { PrimaryButtonFitContainer } from "../../../../../components/Buttons/Buttons";
import { DatePicker } from "../../../../../components/DatePicker/DatePicker";
import { RichEditor } from "../../../../../components/RichEditor/RichEditor";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { Form } from "../../../../../layout/FormLayout";
import type { OptionType, User } from "../../../../../types/types";
import type {
  AttributeSchema,
  CollectionValueRowSchema,
  DocumentAttributeValue,
  IproductReferenceCollectionItem,
  TemplateProductFormStepItemSchema,
} from "../../../../../types/types.PIM";
import {
  getUserName,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { LinkAttributeValueSchema } from "../../../../../util/zod.util";
import { getValidationForProductCollection } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { MultiSelectRefType } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { CollectionSingleSelectListBackedField } from "../../../../../components/AttributeGroupFormGenerator/ListBackedSelectField";
import { shouldUseLazyListChoices } from "../../../../../util/attributeChoices";
import styled from "styled-components";
import { CollectionRow, CollectionValues } from "./NewProductForm";
import { ActionTypes, InputActionMeta } from "react-select";
import { useDependentAttributes } from "../../../../../util/hooks";
import { sortOptionsByLabel } from "../../../../../util/options";
import {
  buildFormattedValuesInColumnOrder,
  getDataTypeForInputType,
  DocumentCollectionFormValue,
} from "./utils";
import { AddressAttributeForm } from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/AddressAttributeForm";
import {
  AddressValue,
  prepareAddressForForm,
} from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";

export function stringArrayToOptionArray(str: string): OptionType<string> {
  return { label: str, value: str };
}

/** Converts collection row values to the record format expected by updateDependentAttributes */
function toEditValues(row: CollectionValueRowSchema): Record<string, unknown> {
  return row.values.reduce((acc, val) => {
    acc[val.attribute_id] = val.value;
    return acc;
  }, {} as Record<string, unknown>);
}

const RichEditorLocalContainer = styled.div`
  .ql-container {
    height: fit-content;
  }
`;

const StyledForm = styled(Form)`
  div[class*="auto-filled-indicator"] {
    > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

type FormOutput = Record<string, unknown>;

const getCustomValidation = ({
  input_type,
  optional,
  t,
  is_multiple_choice,
}: {
  input_type: AttributeSchema["input_type"];
  optional: boolean;
  hasPackagingVolume?: boolean;
  is_multiple_choice: boolean;
  t: TFunction;
}) => {
  return getValidationForProductCollection({
    input_type,
    optional,
    t,
    is_multiple_choice,
  });
};

const getValidationSchema = (columns: AttributeSchema[], t: TFunction) => {
  let validationSchemaObj: {
    [prop: string]:
      | z.ZodString
      | z.ZodBoolean
      | z.ZodSchema
      | z.ZodOptional<z.ZodString | z.ZodBoolean | z.ZodSchema>;
  } = {};

  columns.forEach((col) => {
    validationSchemaObj = {
      ...validationSchemaObj,
      [col.id]: getCustomValidation({
        input_type: col.input_type,
        optional: !col.is_required,
        is_multiple_choice: col.is_multiple_choice ?? false,
        hasPackagingVolume: col.name === "Packaging Volume",
        t,
      }),
    };
  });
  return z.object(validationSchemaObj).superRefine((formValue, ctx) => {
    if (
      Object.values(formValue).every(
        (value: string | boolean | string[] | OptionType<any> | undefined) => {
          if (typeof value === "string") {
            return value === "";
          } else if (Array.isArray(value)) {
            return value.length === 0;
          } else if (typeof value === "object") {
            return !value;
          } else {
            return value === undefined || value === null;
          }
        }
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("Select at least one field"),
        path: [],
      });
    }
  });
};

function buildRowValues(
  formattedValues: ReturnType<typeof buildFormattedValuesInColumnOrder>
) {
  return formattedValues.flatMap(
    ({ attribute_id, input_type, default_values }) => {
      const data_type = getDataTypeForInputType(input_type);
      if (Array.isArray(default_values) && default_values.length > 1) {
        return default_values.map((val) => ({
          id: crypto.randomUUID(),
          priority: 0,
          enum_variant: null,
          attribute_id,
          value: val ?? "",
          data_type,
        }));
      }
      return {
        id: crypto.randomUUID(),
        priority: 0,
        enum_variant: null,
        attribute_id,
        value: default_values?.[0] ?? "",
        data_type,
      };
    }
  );
}

export function AddItemToCollectionForm({
  onSuccess,
  stepItem,
  columns,
  row,
  handleCollectionValues,
  hasReferenceEntityMapping,
  collectionId,
}: {
  onSuccess: () => void;
  stepItem: TemplateProductFormStepItemSchema;
  columns: AttributeSchema[];
  row?: CollectionValueRowSchema | null;
  handleCollectionValues: (collectionValues: CollectionValues) => void;
  hasReferenceEntityMapping?: boolean;
  collectionId?: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const editConstraintsFetched = useRef(false);
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const multiSelectRefs = useRef<{
    [key: string]: MultiSelectRefType;
  }>({});

  // When row exists, merge columns with row values to create attribute-like structure
  const rowAttributes = useMemo(() => {
    return row
      ? columns.map((col) => {
          // For multi-select, we need to find ALL matching values, not just one
          if (
            col.input_type === "multi_select" ||
            col.input_type === "document" ||
            (col.input_type === "user" && col.is_multiple_choice)
          ) {
            const matchingValues = row.values.filter(
              (v) => v.attribute_id === col.id
            );
            return {
              ...col,
              default_values: matchingValues.length > 0 ? matchingValues : null,
            };
          } else {
            // For other field types, find the first matching value
            const matchingValue = row.values.find(
              (v) => v.attribute_id === col.id
            );
            return {
              ...col,
              default_values: matchingValue ? [matchingValue] : null,
            };
          }
        })
      : undefined;
  }, [row, columns]);

  const defaultValues = useMemo(() => {
    return (
      rowAttributes?.reduce(
        (acc, row_attr) => {
          const has_value =
            !!row_attr.default_values && !!row_attr.default_values[0]?.value;
          if (row_attr.input_type === "link") {
            acc[row_attr.id] = has_value
              ? (row_attr.default_values![0]?.value as {
                  url: string;
                  display_text: string;
                })
              : { display_text: "", url: "" };
          } else if (row_attr.input_type === "toggle") {
            acc[row_attr.id] = has_value
              ? (row_attr.default_values![0]?.value as boolean)
              : false;
          } else if (row_attr.input_type === "single_select") {
            const selectValue = has_value
              ? row_attr.default_values![0]?.enum_variant?.variant ??
                row_attr.default_values![0]?.value
              : "";
            acc[row_attr.id] = {
              label: String(selectValue),
              value: selectValue,
            };
          } else if (row_attr.input_type === "user") {
            acc[row_attr.id] = row_attr.is_multiple_choice
              ? (row_attr.default_values
                  ?.map((val) =>
                    val.value
                      ? {
                          label: getUserName(
                            (val.value as User).firstname,
                            (val.value as User).lastname
                          ),
                          value: (val.value as User).id,
                        }
                      : null
                  )
                  .filter((val) => val !== null) as OptionType[])
              : ({
                  label: has_value
                    ? getUserName(
                        (row_attr.default_values![0]?.value as User).firstname,
                        (row_attr.default_values![0]?.value as User).lastname
                      )
                    : "",
                  value: has_value
                    ? (row_attr.default_values![0]?.value as User).id
                    : "",
                } as OptionType);
          } else if (row_attr.input_type === "product_reference") {
            acc[row_attr.id] = row_attr.is_multiple_choice
              ? (row_attr.default_values
                  ?.map((val) =>
                    val.value
                      ? {
                          label: (val.value as IproductReferenceCollectionItem)
                            .name,
                          value: (val.value as IproductReferenceCollectionItem)
                            .id,
                        }
                      : null
                  )
                  .filter((val) => val !== null) as OptionType[])
              : ({
                  label: has_value
                    ? (
                        row_attr.default_values![0]
                          ?.value as IproductReferenceCollectionItem
                      ).name
                    : "",
                  value: has_value
                    ? (
                        row_attr.default_values![0]
                          ?.value as IproductReferenceCollectionItem
                      ).id
                    : "",
                } as OptionType);
          } else if (row_attr.input_type === "object_reference") {
            acc[row_attr.id] = row_attr.is_multiple_choice
              ? (row_attr.default_values
                  ?.map((val) =>
                    val.value
                      ? {
                          label: (val.value as { name: string; id: string })
                            .name,
                          value: (val.value as { name: string; id: string }).id,
                        }
                      : null
                  )
                  .filter((val) => val !== null) as OptionType[])
              : ({
                  label: has_value
                    ? (
                        row_attr.default_values![0]?.value as {
                          name: string;
                          id: string;
                        }
                      ).name
                    : "",
                  value: has_value
                    ? (
                        row_attr.default_values![0]?.value as {
                          name: string;
                          id: string;
                        }
                      ).id
                    : "",
                } as OptionType);
          } else if (row_attr.input_type === "multi_select") {
            acc[row_attr.id] =
              row_attr.default_values?.map((val) => val.value as string) || [];
          } else if (row_attr.input_type === "address") {
            acc[row_attr.id] = has_value
              ? prepareAddressForForm(
                  row_attr.default_values![0]?.value as AddressValue
                )
              : {};
          } else if (row_attr.input_type === "document") {
            acc[row_attr.id] = has_value
              ? row_attr
                  .default_values!.filter(
                    (dv) => (dv.value as DocumentAttributeValue)?.asset_id
                  )
                  .map((dv) => ({
                    asset_id: (dv.value as DocumentAttributeValue).asset_id,
                    value_id: dv.id,
                  }))
              : [];
          } else {
            acc[row_attr.id] = String(
              has_value ? row_attr.default_values![0]?.value : ""
            );
          }

          return acc;
        },
        {} as {
          [key: string]:
            | OptionType
            | OptionType[]
            | string
            | boolean
            | undefined
            | string[]
            | { display_text: string; url: string }
            | DocumentCollectionFormValue[]
            | Record<string, unknown>;
        }
      ) ??
      columns.reduce(
        (acc, col) => {
          switch (col.input_type) {
            case "checkbox":
            case "toggle":
              acc[col.id] = false;
              break;
            case "single_select":
              // Use undefined for empty select to prevent validation on mount
              acc[col.id] = col.is_required
                ? undefined
                : { label: "", value: "" };
              break;
            case "product_reference":
            case "object_reference":
              // Use undefined for empty select to prevent validation on mount
              acc[col.id] = col.is_required
                ? undefined
                : col.is_multiple_choice
                ? []
                : { label: "", value: "" };
              break;
            case "user":
              acc[col.id] = col.is_required
                ? undefined
                : col.is_multiple_choice
                ? []
                : { label: "", value: "" };
              break;
            case "multi_select":
              // Use undefined for empty multi-select to prevent validation on mount
              acc[col.id] = col.is_required ? undefined : [];
              break;
            case "link":
              // Use undefined for empty link to prevent validation on mount
              acc[col.id] = col.is_required
                ? undefined
                : { display_text: "", url: "" };
              break;
            case "date":
              // Use undefined for empty date to prevent validation on mount
              acc[col.id] = col.is_required ? undefined : "";
              break;
            case "numeric":
              // Use undefined for empty numeric to prevent validation on mount
              acc[col.id] = col.is_required ? undefined : "";
              break;
            case "address":
              acc[col.id] = col.is_required ? undefined : {};
              break;
            case "document":
              acc[col.id] = col.is_required ? undefined : [];
              break;
            default:
              acc[col.id] = col.is_required ? undefined : "";
          }
          return acc;
        },
        {} as {
          [key: string]:
            | OptionType
            | OptionType[]
            | string
            | boolean
            | undefined
            | string[]
            | { display_text: string; url: string }
            | DocumentCollectionFormValue[]
            | Record<string, unknown>;
        }
      )
    );
  }, [rowAttributes, columns]);

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(getValidationSchema(columns, t)),
    defaultValues,
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState,
    errors,
    watch,
  } = methodsOfUseForm;

  const {
    clearableAttributes,
    dependentAttributesOptions,
    updateDependentAttributes,
  } = useDependentAttributes({
    mode: "single",
    storefrontId: storefront_id,
    enabled: !!hasReferenceEntityMapping && !!collectionId,
    contextType: "collection",
    contextId: collectionId ?? "",
    getFormValues: watch,
    setFormValue: setValue,
    attributesOrColumns: columns,
    multiSelectRefs,
    multiSelectValueFormat: "string",
  });

  useEffect(() => {
    if (hasReferenceEntityMapping && !editConstraintsFetched.current) {
      editConstraintsFetched.current = true;
      updateDependentAttributes(
        !!row ? { clonedSelectionsValues: toEditValues(row) } : undefined
      );
    }
  }, [hasReferenceEntityMapping, row, updateDependentAttributes]);

  // Helper function to format label with required asterisk
  const formatLabel = useCallback(
    (col: AttributeSchema) => {
      const label = col.display_name ? t([col.display_name]) : col.name;
      return col.is_required ? `${label} *` : label;
    },
    [t]
  );

  // Memoize multi-select attributes to prevent re-rendering
  const memoizedMultiSelectAttributes = useMemo(() => {
    const result: { [key: string]: AttributeSchema } = {};
    columns.forEach((col) => {
      if (col.input_type === "multi_select") {
        const rowAttr = rowAttributes?.find((attr) => attr.name === col.name);
        result[col.id] = {
          ...(rowAttr || col),
          is_editable: true,
        };
      }
    });
    return result;
  }, [columns, rowAttributes]);

  const isClearable = useCallback(
    (col_id: string) => {
      return hasReferenceEntityMapping
        ? clearableAttributes?.has(col_id) ||
            (clearableAttributes || new Set()).size === 0
        : true;
    },
    [clearableAttributes, hasReferenceEntityMapping]
  );

  const isAutofilled = useCallback(
    (col_id: string) => {
      const constraint = dependentAttributesOptions?.find(
        (c) => c.attribute_id === col_id
      );
      return constraint?.action === "auto_fill";
    },
    [dependentAttributesOptions]
  );

  const createForm = (columns: AttributeSchema[]) => {
    // eslint-disable-next-line array-callback-return
    return columns
      .filter(
        (col) =>
          stepItem.attributes.find((attr) => attr.name === col.name) !==
          undefined
      )
      .map((col) => {
        if (col.input_type === "form_field") {
          return (
            <TextField
              name={col.id}
              label={formatLabel(col)}
              theref={register({
                required: col.is_required,
              })}
              formState={formState}
              errors={errors}
              type="text"
              key={col.id}
            />
          );
        } else if (col.input_type === "single_select") {
          const refEntityConstraints = dependentAttributesOptions?.find(
            (c) => c.attribute_id === col.id
          );
          const hasRefMappingForCol =
            !!hasReferenceEntityMapping && refEntityConstraints !== undefined;
          const dependentChoices = sortOptionsByLabel(
            refEntityConstraints?.valid_values?.map((v) => ({
              label: v.display,
              value: v.display,
            })) ?? []
          );
          const singleSelectDefaultValue = (defaultValues?.[col.id] as
            | OptionType<string>
            | undefined) ?? {
            label: "",
            value: "",
          };

          if (
            tenant_id &&
            shouldUseLazyListChoices(col, {
              hasReferenceEntityMapping: hasRefMappingForCol,
              validValues: refEntityConstraints?.valid_values,
            })
          ) {
            return (
              <CollectionSingleSelectListBackedField
                key={col.id}
                col={{ ...col, is_editable: true }}
                tenantId={tenant_id}
                defaultValue={singleSelectDefaultValue}
                methodsOfUseForm={methodsOfUseForm}
                errors={errors}
                t={t}
                is_single_select_disabled={false}
                dropdownZ={0}
                isPackagingTypeField={false}
                onPackagingTypeChange={(selectedValue, onChange) =>
                  onChange(selectedValue)
                }
              />
            );
          }

          const choices = col?.choices?.map(stringArrayToOptionArray) ?? [];
          const singleSelectOptions =
            hasRefMappingForCol && dependentChoices
              ? dependentChoices
              : choices;

          return (
            <Controller
              control={control}
              name={col.id}
              defaultValue={singleSelectDefaultValue}
              key={col.id}
              render={({ onChange, value }) => (
                <SelectBoxV2
                  name={col.id}
                  isClearable={isClearable(col.id)}
                  isAutofilled={isAutofilled(col.id)}
                  placeholder={formatLabel(col)}
                  options={singleSelectOptions}
                  value={value}
                  onChange={(
                    option: { label: string; value: string } | null,
                    action: InputActionMeta
                  ) => {
                    onChange(option, { shouldValidate: true });
                    if (hasReferenceEntityMapping) {
                      updateDependentAttributes({
                        attributeId: col.id,
                        clearSelection:
                          (action.action as ActionTypes) === "clear",
                      });
                    }
                  }}
                  errors={errors}
                  formState={formState}
                />
              )}
            />
          );
        } else if (col.input_type === "multi_select") {
          const refEntityConstraints = dependentAttributesOptions?.find(
            (c) => c.attribute_id === col.id
          );
          const hasRefMappingForCol =
            !!hasReferenceEntityMapping && refEntityConstraints !== undefined;

          if (
            tenant_id &&
            shouldUseLazyListChoices(col, {
              hasReferenceEntityMapping: hasRefMappingForCol,
              validValues: refEntityConstraints?.valid_values,
            })
          ) {
            return (
              <MultiSelectField
                type="collection"
                attribute={memoizedMultiSelectAttributes[col.id]}
                methodsOfUseForm={methodsOfUseForm}
                key={col.id}
                displayByName
                multiSelectRefs={multiSelectRefs}
                isNotApplicable={false}
                isDisabled={isAutofilled(col.id)}
                updateDependentAttributes={
                  hasReferenceEntityMapping
                    ? updateDependentAttributes
                    : undefined
                }
              />
            );
          }

          const multiSelectDependentChoices = hasRefMappingForCol
            ? sortOptionsByLabel(
                refEntityConstraints?.valid_values?.map((v) => ({
                  label: v.display,
                  value: v.display,
                })) ?? []
              )
            : undefined;

          return (
            <MultiSelectField
              type={"collection"}
              attribute={memoizedMultiSelectAttributes[col.id]}
              methodsOfUseForm={methodsOfUseForm}
              key={col.id}
              displayByName={true}
              multiSelectRefs={multiSelectRefs}
              isNotApplicable={false}
              isDisabled={isAutofilled(col.id)}
              dependent_choices={multiSelectDependentChoices}
              updateDependentAttributes={
                hasReferenceEntityMapping
                  ? updateDependentAttributes
                  : undefined
              }
            />
          );
        } else if (col.input_type === "numeric") {
          return (
            <TextField
              name={col.id}
              label={`${formatLabel(col)} (number)`}
              theref={register({
                required: col.is_required,
              })}
              formState={formState}
              errors={errors}
              type="number"
              key={col.id}
            />
          );
        } else if (col.input_type === "multiline_entry") {
          return (
            <RichEditorLocalContainer>
              <RichEditor
                value={(defaultValues?.[col.id] as string) ?? ""}
                id={col.id}
                name={col.id}
                label={formatLabel(col)}
                useFormMethods={{
                  register,
                  formState,
                  setValue,
                  errors,
                  watch,
                }}
                key={col.id}
              />
            </RichEditorLocalContainer>
          );
        } else if (col.input_type === "date") {
          return (
            <DatePicker
              label={formatLabel(col)}
              name={col.id}
              defaultValue={defaultValues?.[col.id] as string}
              isOutsideRange={() => false}
              required={col.is_required}
              methodsOfUseForm={methodsOfUseForm}
              key={col.id}
            />
          );
        } else if (
          col.input_type === "toggle" ||
          col.input_type === "checkbox"
        ) {
          return (
            <ToggleSwitchV2
              label={formatLabel(col)}
              name={col.id}
              ref={register()}
              key={col.id}
            />
          );
        } else if (col.input_type === "product_reference") {
          return (
            <ProductReferenceField
              attribute={{ ...col, is_editable: true }}
              methodsOfUseForm={methodsOfUseForm}
              isNotApplicable={false}
              isDisabled={isAutofilled(col.id)}
            />
          );
        } else if (col.input_type === "object_reference") {
          const rawDefaults =
            rowAttributes?.find((attr) => attr.id === col.id)?.default_values ??
            col.default_values ??
            [];
          const objectRefValues = rawDefaults.map((dv) => {
            if (dv.object_ref) return dv;
            const val = dv.value as
              | { id: string; name: string }
              | null
              | undefined;
            return val && typeof val === "object"
              ? { ...dv, object_ref: { id: val.id, name: val.name } }
              : dv;
          });
          return (
            <ObjectReferenceField
              attribute={
                {
                  ...col,
                  is_editable: true,
                  values: objectRefValues,
                } as Extract<
                  AttributeSchema,
                  { input_type: "object_reference" }
                >
              }
              methodsOfUseForm={methodsOfUseForm}
              isDisabled={isAutofilled(col.id)}
              isNotApplicable={false}
            />
          );
        } else if (col.input_type === "user") {
          // Get filtered options for user field from reference entity constraints
          const userDependentOptions = hasReferenceEntityMapping
            ? dependentAttributesOptions
                ?.find((c) => c.attribute_id === col.id)
                ?.valid_values?.map((v) => ({
                  label: v.display,
                  value: v.display,
                }))
            : undefined;

          return (
            <UserReferenceField
              attribute={{ ...col, is_editable: true }}
              methodsOfUseForm={methodsOfUseForm}
              isNotApplicable={false}
              isDisabled={isAutofilled(col.id)}
              dependent_attributes_options={userDependentOptions}
              updateDependentAttributes={
                hasReferenceEntityMapping
                  ? updateDependentAttributes
                  : undefined
              }
            />
          );
        } else if (col.input_type === "link") {
          const maybeDefaultValue = (() => {
            if (defaultValues?.[col.id]) {
              const parsed = LinkAttributeValueSchema.safeParse(
                defaultValues?.[col.id]
              );
              if (parsed.success) {
                return parsed.data as { url: string; display_text: string };
              }
            }
            return { url: "", display_text: "" };
          })();

          return (
            <LinkAttributeField
              key={col.id}
              name={col.id}
              label={formatLabel(col)}
              control={control}
              register={register}
              formState={formState}
              errors={errors}
              defaultValue={maybeDefaultValue}
              isRequired={col.is_required}
              layout="vertical"
            />
          );
        } else if (col.input_type === "address") {
          const addressCol = col as Extract<
            AttributeSchema,
            { input_type: "address" }
          >;
          const savedValue = defaultValues?.[col.id]
            ? prepareAddressForForm(defaultValues[col.id] as AddressValue)
            : undefined;

          return (
            <FormProvider {...methodsOfUseForm}>
              <AddressAttributeForm
                key={col.id}
                fieldPrefix={col.id}
                mode={addressCol.address_mode ?? "standard"}
                isNotApplicable={col.is_not_applicable ?? false}
                savedValue={savedValue}
                attributeName={formatLabel(col)}
              />
            </FormProvider>
          );
        } else if (col.input_type === "document") {
          const existingRowAttr = rowAttributes?.find(
            (attr) => attr.id === col.id
          );
          return (
            <DocumentAttribute
              key={col.id}
              attribute={{
                ...col,
                is_editable: true,
                values:
                  existingRowAttr?.default_values ??
                  col.values ??
                  col.default_values ??
                  [],
              }}
              methodsOfUseForm={methodsOfUseForm}
              isDisabled={isAutofilled(col.id)}
              isNotApplicable={false}
              uploadNewDocumentsOnly
              hideAccessibility
            />
          );
        } else {
          return null;
        }
      });
  };

  const onSubmit = async (values: FormOutput) => {
    // the order of the object keys after submission is not deterministic, in
    // order for the API call to succeed the rows must be in the same order as
    // the columns.
    setIsLoading(true);

    const formattedValues = buildFormattedValuesInColumnOrder(columns, values);

    const rowValues = buildRowValues(formattedValues);

    if (row) {
      handleCollectionValues([
        {
          method: "patch",
          collection_id: stepItem.group_or_collection_id,
          row_id: row.row_id,
          rows: [{ ...row, values: rowValues }],
        },
      ]);
    } else {
      handleCollectionValues([
        {
          method: "post",
          collection_id: stepItem.group_or_collection_id,
          rows: [
            {
              row_id: crypto.randomUUID(),
              values: rowValues,
              is_master: false,
              is_editable: true,
            } as CollectionRow,
          ],
        },
      ]);
    }

    onSuccess();
  };

  return (
    <>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        {createForm(rowAttributes || columns)}
        <PrimaryButtonFitContainer
          disabled={isLoading}
          loading={isLoading}
          type="submit"
          style={{ marginTop: "10px" }}
        >
          {t("Save")}
        </PrimaryButtonFitContainer>
      </StyledForm>
    </>
  );
}
